import { useQuery } from '@tanstack/vue-query';
import type { QueryClient } from '@tanstack/vue-query';
import { ApiError } from '@/api/errors';
import { request } from '@/api/request';
import { mergeQueryOptions, type QueryOptions } from '@/modules/query/utils';
import { useMode } from '@/modules/mode/composables';
import { isAuthenticatedByRLRef } from '@/modules/auth/state';
import { OrganisationOwnershipStatus } from '@@/packages/shared/types/models/organisation';
import { TRIAL_QUERY } from './queryKeys';

export type TrialType = 'TRIAL' | 'SELF_TRIAL' | 'PLG_TRIAL';

// Which suppliers a client on a trial may send connection requests to. The
// backend enforces the same rule on POST /connection-requests (403 otherwise).
export type TrialConnectionPrivilege = 'managed_only' | 'managed_and_selected' | 'all';

// Get current orgs trial
export type TrialResponse = {
  id: string;
  orgID: string | null;
  createdBy: string;
  createdAt: string;
  trialName: string;
  connectionPrivilege: TrialConnectionPrivilege;
  inviteRestrictions: boolean;
  approvedSuppliers: string[] | null;
  concludedAt: string | null;
  concludedBy: string | null;
  trialEndsAt: string | null;
  trialType: TrialType;
};

const isDateInPast = (date: string) => new Date().getTime() > new Date(date).getTime();

export function useTrial(options?: QueryOptions, queryClient?: QueryClient) {
  const query = useQuery(
    mergeQueryOptions(options, {
      queryKey: [TRIAL_QUERY],
      async queryFn() {
        try {
          const res = await request<TrialResponse>('GET', '/trial');

          if (res.status === 204) {
            return false;
          }

          return res.data;
        } catch (err) {
          if (err instanceof ApiError && err.response?.status === 404) return false;
          throw err;
        }
      },
      enabled: isAuthenticatedByRLRef,
    }),
    queryClient,
  );

  const trial = query.data;

  const canInvite = computed(() => {
    // Only active or ended (expired) trials are returned so concluded does not
    // need to be considered. Concluded is the same as not being on a trial.
    if (!trial.value) return true;

    // If the org is on a trial that has ended they are blocked from inviting.
    if (trial.value.trialEndsAt && isDateInPast(trial.value.trialEndsAt)) return false;

    return !trial.value.inviteRestrictions;
  });

  const { currentMode } = useMode();
  function canConnect(orgId: string, ownershipStatus: OrganisationOwnershipStatus) {
    // Connection restrictions only apply to clients
    if (currentMode.value !== 'client') return true;

    // Not on a trial
    if (!trial.value || trial.value.concludedAt) return true;

    // Trial has expired
    if (trial.value.trialEndsAt && isDateInPast(trial.value.trialEndsAt)) return false;

    const isManaged = ownershipStatus === OrganisationOwnershipStatus.Managed;
    switch (trial.value.connectionPrivilege) {
      case 'all':
        return true;
      case 'managed_only':
        return isManaged;
      case 'managed_and_selected':
        return isManaged || (trial.value.approvedSuppliers?.includes(orgId) ?? false);
      default:
        // Fail closed: offering a connection the backend then rejects leaves the
        // user with a 403 at the end of the flow, after filling every step.
        return false;
    }
  }

  const isOnActiveTrial = computed(
    () =>
      !!trial.value &&
      !trial.value.concludedAt &&
      !(trial.value.trialEndsAt && isDateInPast(trial.value.trialEndsAt)),
  );

  const isTrialEnded = computed(
    () =>
      !!trial.value &&
      !trial.value.concludedAt &&
      !!trial.value.trialEndsAt &&
      isDateInPast(trial.value.trialEndsAt),
  );

  const isOnTrialType = (trialType: TrialType) =>
    isOnActiveTrial.value && !!trial.value && trial.value.trialType === trialType;

  return { ...query, trial, canInvite, canConnect, isOnActiveTrial, isTrialEnded, isOnTrialType };
}
